import { type DragEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, OctagonAlert, Truck, UserPlus, UserRound } from 'lucide-react';
import type {
  KanbanCard as Card,
  StageConfigurationView,
  WorkflowStage,
} from '@tailoring-crm/shared';
import { formatDate, formatRelativeTime } from '../../lib/format.js';
import { cn, Badge, Button } from '@tailoring-crm/ui';
import { CardThumbnail } from './card-thumbnail.js';
import { StageControl } from './stage-control.js';

/**
 * One garment on the board.
 *
 * The card answers, in one glance and in this order: is it late, whose is it, what is it, and
 * when is it due. Lateness is a border and a badge rather than only a colour, because "colour
 * or badge overdue items" has to survive a bright shop counter, a cheap screen and colour
 * blindness — the border is a second, redundant signal on purpose.
 *
 * It is kept SHORT, because a column that shows two cards before it scrolls is a column nobody
 * reads down. The karigar's name appears once — on the assign control, which is also how the
 * garment is handed to somebody else — and every action shares one row. The photo appears only
 * when there is one: a placeholder on every unphotographed garment is width taken from the name.
 */
export interface KanbanCardProps {
  card: Card;
  configuration: StageConfigurationView | undefined;
  selected: boolean;
  onToggleSelected: (itemId: string) => void;
  onAdvance: (card: Card, toStage: WorkflowStage) => void;
  onRevert: (card: Card, toStage: WorkflowStage) => void;
  /** Omitted for a caller who may not hand work out; the control then does not appear. */
  onAssign?: (card: Card) => void;
  canRevert?: boolean;
  busy?: boolean;
}

const DUE_STYLES: Record<string, string> = {
  overdue: 'border-destructive/60 bg-destructive/5',
  today: 'border-warning/60 bg-warning/5',
  upcoming: 'border-border',
  none: 'border-border',
};

export function KanbanCardTile({
  card,
  configuration,
  selected,
  onToggleSelected,
  onAdvance,
  onRevert,
  onAssign,
  canRevert = true,
  busy = false,
}: KanbanCardProps): ReactElement {
  const { t } = useTranslation();

  const onDragStart = (event: DragEvent<HTMLLIElement>): void => {
    // The payload is the item id; the drop target reads it back out. Plain text so it works in
    // every browser without a custom MIME negotiation.
    event.dataTransfer.setData('text/plain', card.itemId);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <li
      draggable={!busy}
      onDragStart={onDragStart}
      data-item-id={card.itemId}
      data-stage={card.stage}
      className={cn(
        'flex flex-col gap-1.5 rounded-lg border p-2.5 transition-colors',
        DUE_STYLES[card.dueState] ?? 'border-border',
        selected && 'ring-2 ring-ring',
        busy && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelected(card.itemId)}
          aria-label={t('kanban.selectCard', {
            garment: card.garmentTypeLabel,
            order: card.orderNumber,
          })}
          className="mt-1 size-4 shrink-0 accent-primary"
        />

        {card.thumbnailMediaId ? (
          <CardThumbnail mediaId={card.thumbnailMediaId} alt={card.garmentTypeLabel} />
        ) : null}

        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{card.customerName}</p>
          <p className="flex min-w-0 items-baseline gap-1.5 text-sm text-muted-foreground">
            <span className="truncate">
              {card.quantity} × {card.garmentTypeLabel}
            </span>
            <span aria-hidden="true">·</span>
            <Link
              to={`/orders/${card.orderId}`}
              className="shrink-0 text-xs underline-offset-2 hover:underline"
            >
              {card.orderNumber}
            </Link>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {card.priority ? <Badge variant="destructive">{t('orders.urgent')}</Badge> : null}

        {card.dueState === 'overdue' ? (
          <Badge variant="destructive">
            <AlertTriangle aria-hidden="true" className="mr-1 size-3" />
            {t('orders.overdueBy', { date: formatDate(card.promisedDate ?? '') })}
          </Badge>
        ) : card.dueState === 'today' ? (
          <Badge variant="warning">{t('orders.dueToday')}</Badge>
        ) : card.promisedDate ? (
          <span className="text-muted-foreground">{formatDate(card.promisedDate)}</span>
        ) : (
          <span className="text-muted-foreground">{t('orders.noPromisedDate')}</span>
        )}

        <span className="flex items-center gap-1 text-muted-foreground">
          <Clock aria-hidden="true" className="size-3" />
          {t('kanban.inStageSince', { since: formatRelativeTime(card.stageEnteredAt) })}
        </span>

        {/*
          Whose garment it is. A card with no name has not been handed to anybody — which is a
          state the owner needs to be able to see at a glance on a busy morning, so the badge is
          simply absent rather than reading "Unassigned" a hundred times down a column. Where
          the card has an assign control, that control carries the name instead.
        */}
        {card.assigneeName && !onAssign ? (
          <Badge variant="outline">{card.assigneeName}</Badge>
        ) : null}

        {/* Somebody has stopped on this garment. Red, and next to the name, because the two
            facts belong together: who has it, and why it is not moving. */}
        {card.hasProblem ? (
          <Badge variant="destructive">
            <OctagonAlert aria-hidden="true" className="mr-1 size-3" />
            {t('assignments.problemBadge')}
          </Badge>
        ) : null}

        {/*
          Off the premises (task 29). Without this, a garment away at an embroiderer's for a
          fortnight reads as one that has been sitting in the same column untouched — which is
          the single wrong conclusion the board could lead somebody to on a busy morning.
        */}
        {card.outsourcedTo ? (
          <Badge variant="outline">
            <Truck aria-hidden="true" className="mr-1 size-3" />
            {card.outsourcedDays === null
              ? card.outsourcedTo
              : t('outsourcing.cardBadge', {
                  vendor: card.outsourcedTo,
                  count: card.outsourcedDays,
                })}
          </Badge>
        ) : null}
      </div>

      {/* Every action on one row: the next stage, who has it, and the send-back menu. */}
      <div className="flex min-w-0 items-center gap-1">
        <StageControl
          card={card}
          configuration={configuration}
          canRevert={canRevert}
          disabled={busy}
          onAdvance={(toStage) => onAdvance(card, toStage)}
          onRevert={(toStage) => onRevert(card, toStage)}
        />

        {onAssign ? (
          // Unassigned reads as an instruction; assigned reads as the name, and tapping the
          // name is how the garment goes to somebody else.
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onAssign(card)}
            className="ml-auto min-w-0 gap-1.5"
            {...(card.assigneeName
              ? {
                  'aria-label': t('assignments.reassignAction', { name: card.assigneeName }),
                  // A narrow column cuts the name short; hovering still says whose it is.
                  title: t('assignments.reassignAction', { name: card.assigneeName }),
                }
              : {})}
          >
            {card.assigneeName ? (
              <UserRound aria-hidden="true" className="size-3.5 shrink-0" />
            ) : (
              <UserPlus aria-hidden="true" className="size-3.5 shrink-0" />
            )}
            <span className="truncate">{card.assigneeName ?? t('assignments.assignAction')}</span>
          </Button>
        ) : null}
      </div>
    </li>
  );
}
